import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PopupAudience, PopupContent } from '@promo-popup-contract';
import {
  AdminPopupView,
  getAdminPopup,
  getMyPopup,
  publishPopup,
  savePopupDraft,
  setPopupVisibility,
  uploadPopupImage,
} from '@/api/promoPopup';

export const adminPopupKey = (audience: PopupAudience) => [
  'promo-popup',
  'admin',
  audience,
];
export const MY_POPUP_KEY = ['promo-popup', 'me'];

export const useAdminPopup = (audience: PopupAudience) =>
  useQuery<AdminPopupView>({
    queryKey: adminPopupKey(audience),
    queryFn: () => getAdminPopup(audience),
    // TCX-917: the editor must always seed from authoritative server state, never
    // a cached copy still holding a since-published draft. Same stance
    // useLegalChannel takes, for the same reason.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

export const useMyPopup = () => {
  // TCX-917: `/consultants/:id` and `/franchisors/:id` are public routes
  // (withAuth.tsx) that render this component's ancestor for logged-out
  // visitors too. An unauthenticated GET /promo-popup/me comes back 401 (the
  // refresh-token middleware doesn't allowlist it), and axiosInstance's
  // interceptor treats any 401 with no refresh token as a dead session: it
  // clears localStorage/sessionStorage and hard-redirects to /sign-in. So this
  // query must never even run for an anonymous visitor — same "should I fetch
  // at all" gate Header.tsx's notifications query and useAppSettings.ts use,
  // both keyed off the same accessToken presence check.
  const hasSession =
    typeof window !== 'undefined' && !!localStorage.getItem('accessToken');

  return useQuery<PopupContent | null>({
    queryKey: MY_POPUP_KEY,
    queryFn: getMyPopup,
    enabled: hasSession,
    // What this does and does NOT do (TCX-917 review, finding 3). `staleTime`
    // marks the data stale after a minute; it does not schedule a fetch.
    // Nothing here polls — there is deliberately no `refetchInterval`, which
    // would have every authenticated tab hitting this endpoint once a minute
    // for a promotional pop-up.
    //
    // So: turning the pop-up off stops it being SERVED immediately (the
    // server returns null on the next request, AC 22), and a tab that is
    // already open picks that up on its next refetch trigger — in practice
    // React Query's `refetchOnWindowFocus`, i.e. when the viewer leaves the
    // tab and comes back. A viewer who keeps this tab focused and has no
    // auto-dismiss timer configured keeps the pop-up until they do.
    //
    // The minute is what stops a burst of triggers (focus, remount, network
    // reconnect) turning into a burst of requests.
    staleTime: 60 * 1000,
  });
};

export const useSavePopupDraft = (audience: PopupAudience) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { content: PopupContent; imageReceipt?: string }) =>
      savePopupDraft(audience, vars.content, vars.imageReceipt),
    onSuccess: (view) => queryClient.setQueryData(adminPopupKey(audience), view),
  });
};

export const usePublishPopup = (audience: PopupAudience) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => publishPopup(audience),
    onSuccess: (view) => {
      queryClient.setQueryData(adminPopupKey(audience), view);
      // TCX-917: what users receive just changed.
      queryClient.invalidateQueries({ queryKey: MY_POPUP_KEY });
    },
  });
};

export const useSetPopupVisibility = (audience: PopupAudience) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (isDisabled: boolean) => setPopupVisibility(audience, isDisabled),
    onSuccess: (view) => {
      queryClient.setQueryData(adminPopupKey(audience), view);
      // TCX-917: what users receive just changed.
      queryClient.invalidateQueries({ queryKey: MY_POPUP_KEY });
    },
  });
};

export const useUploadPopupImage = (audience: PopupAudience) =>
  useMutation({ mutationFn: (file: File) => uploadPopupImage(audience, file) });
